import { useState, useEffect, useCallback, createContext, useContext, useRef } from "react";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

// ============================================================
// CONFIG
// ============================================================
const BASE_URL = "http://localhost:8787/api/v1";
// ↑ Replace with your actual CF Worker URL

// ============================================================
// API LAYER
// ============================================================
class ApiError extends Error {
  constructor(message, status, errors = []) {
    super(message);
    this.status = status;
    this.errors = errors;
  }
}

function getToken() { return localStorage.getItem("hrms_token"); }

async function api(path, opts = {}) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opts.headers,
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 204) return null;
  const data = await res.json();
  if (!res.ok) throw new ApiError(data.message || "Error", res.status, data.errors || []);
  return data;
}

const authApi = {
  login:  (b) => api("/auth/login",  { method: "POST", body: b }),
  logout: ()  => api("/auth/logout", { method: "POST" }),
};
const usersApi = {
  list:          (p = {}) => api(`/users?${new URLSearchParams(p)}`),
  create:        (b)      => api("/users", { method: "POST", body: b }),
  getMe:         ()       => api("/users/me"),
  update:        (id, b)  => api(`/users/${id}`, { method: "PUT", body: b }),
  toggleActive:  (id)     => api(`/users/${id}/activate`, { method: "PATCH" }),
  assignManager: (id, b)  => api(`/employees/${id}/assign-manager`, { method: "PUT", body: b }),
};
const cyclesApi = {
  list:         (p = {}) => api(`/cycles?${new URLSearchParams(p)}`),
  create:       (b)      => api("/cycles", { method: "POST", body: b }),
  get:          (id)     => api(`/cycles/${id}`),
  changeStatus: (id, s)  => api(`/cycles/${id}/status`, { method: "PATCH", body: { status: s } }),
};
const reviewsApi = {
  list:          (p = {}) => api(`/reviews?${new URLSearchParams(p)}`),
  create:        (b)      => api("/reviews", { method: "POST", body: b }),
  get:           (id)     => api(`/reviews/${id}`),
  saveSelf:      (id, b)  => api(`/reviews/${id}/self`, { method: "PUT", body: b }),
  submit:        (id)     => api(`/reviews/${id}/submit`, { method: "PATCH" }),
  managerReview: (id, b)  => api(`/reviews/${id}/manager`, { method: "PUT", body: b }),
  finalize:      (id, b)  => api(`/reviews/${id}/finalize`, { method: "PATCH", body: b }),
  getHistory:    (id)     => api(`/reviews/${id}/history`),
};
const analyticsApi = {
  summary: () => api("/analytics/summary"),
  cycle:   (id) => api(`/analytics/cycle/${id}`),
};
const notifApi = {
  list:       (p = {}) => api(`/notifications?${new URLSearchParams(p)}`),
  markRead:   (id)     => api(`/notifications/${id}/read`, { method: "PATCH" }),
  markAllRead:()       => api("/notifications/read", { method: "PATCH" }),
};

// ============================================================
// AUTH CONTEXT
// ============================================================
const AuthCtx = createContext(null);
function useAuth() { return useContext(AuthCtx); }

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hrms_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const res = await authApi.login({ email, password });
      localStorage.setItem("hrms_token", res.data.access_token);
      localStorage.setItem("hrms_user", JSON.stringify(res.data.user));
      setUser(res.data.user);
      return res.data.user;
    } finally { setLoading(false); }
  };

  const logout = async () => {
    try { await authApi.logout(); } catch {}
    localStorage.removeItem("hrms_token");
    localStorage.removeItem("hrms_user");
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, login, logout, loading }}>{children}</AuthCtx.Provider>;
}

// ============================================================
// TOAST SYSTEM
// ============================================================
const ToastCtx = createContext(null);
function useToast() { return useContext(ToastCtx); }

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={{ toast: add }}>
      {children}
      <div style={{ position: "fixed", top: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10 }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            background: t.type === "error" ? "#fee2e2" : t.type === "warn" ? "#fef3c7" : "#dcfce7",
            border: `1px solid ${t.type === "error" ? "#fca5a5" : t.type === "warn" ? "#fcd34d" : "#86efac"}`,
            color: t.type === "error" ? "#991b1b" : t.type === "warn" ? "#92400e" : "#166534",
            padding: "12px 20px", borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif",
            boxShadow: "0 4px 24px rgba(0,0,0,0.12)", maxWidth: 360,
            animation: "slideIn 0.3s ease",
          }}>{t.msg}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ============================================================
// DESIGN TOKENS & STYLES
// ============================================================
const C = {
  bg:      "#0f1117",
  surface: "#181c25",
  card:    "#1e2333",
  border:  "#2a3147",
  accent:  "#6366f1",
  accentL: "#818cf8",
  accentD: "#4f46e5",
  success: "#10b981",
  warn:    "#f59e0b",
  danger:  "#ef4444",
  muted:   "#64748b",
  text:    "#e2e8f0",
  textSub: "#94a3b8",
};

const STATUS_META = {
  draft:             { label: "Draft",             color: C.muted,   bg: "#1e2333" },
  submitted:         { label: "Submitted",         color: "#f59e0b", bg: "#451a03" },
  manager_reviewed:  { label: "Manager Reviewed",  color: "#6366f1", bg: "#1e1b4b" },
  finalized:         { label: "Finalized",         color: "#10b981", bg: "#022c22" },
};
const DECISION_META = {
  approved:          { label: "Approved",          color: "#10b981", bg: "#022c22" },
  needs_improvement: { label: "Needs Improvement", color: "#f59e0b", bg: "#451a03" },
  rejected:          { label: "Rejected",          color: "#ef4444", bg: "#450a0a" },
};

const gs = {
  page:   { background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'DM Sans', sans-serif" },
  card:   { background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 },
  input:  {
    background: "#0f1117", border: `1px solid ${C.border}`, borderRadius: 10,
    color: C.text, padding: "10px 14px", fontSize: 14, width: "100%", outline: "none",
    fontFamily: "'DM Sans', sans-serif", transition: "border-color 0.2s",
  },
  btn: (variant = "primary", size = "md") => ({
    background: variant === "primary" ? C.accent : variant === "success" ? C.success : variant === "danger" ? C.danger : "transparent",
    border: variant === "ghost" ? `1px solid ${C.border}` : "none",
    color: "#fff", borderRadius: 10, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600, fontSize: size === "sm" ? 13 : 14,
    padding: size === "sm" ? "7px 14px" : "10px 20px",
    transition: "all 0.2s", display: "inline-flex", alignItems: "center", gap: 6,
  }),
};

// ============================================================
// GLOBAL CSS
// ============================================================
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${C.bg}; color: ${C.text}; }
    ::-webkit-scrollbar { width: 6px; } 
    ::-webkit-scrollbar-track { background: ${C.surface}; }
    ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
    @keyframes slideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
    @keyframes fadeUp  { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes spin    { to { transform: rotate(360deg); } }
    .fade-up { animation: fadeUp 0.4s ease; }
    select option { background: #1e2333; color: #e2e8f0; }
    textarea { resize: vertical; }
    a { color: inherit; text-decoration: none; }
  `}</style>
);

// ============================================================
// SHARED COMPONENTS
// ============================================================
function Badge({ status, type = "status" }) {
  const meta = (type === "decision" ? DECISION_META : STATUS_META)[status] || { label: status, color: C.muted, bg: C.card };
  return (
    <span style={{
      background: meta.bg, color: meta.color, border: `1px solid ${meta.color}33`,
      borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
    }}>{meta.label}</span>
  );
}

function Spinner({ size = 24 }) {
  return <div style={{ width: size, height: size, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.accent}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />;
}

function StatCard({ label, value, sub, icon, color = C.accent }) {
  return (
    <div style={{ ...gs.card, display: "flex", alignItems: "flex-start", gap: 16 }}>
      <div style={{ background: `${color}22`, borderRadius: 12, padding: 12, fontSize: 22 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color }}>{value}</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function Modal({ open, onClose, title, children, width = 560 }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, width, maxWidth: "100%", maxHeight: "90vh", overflow: "auto", animation: "fadeUp 0.25s ease" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, error, children, required }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: C.textSub }}>
        {label}{required && <span style={{ color: C.danger }}> *</span>}
      </label>
      {children}
      {error && <span style={{ fontSize: 12, color: C.danger }}>{error}</span>}
    </div>
  );
}

function RatingStars({ value, onChange, readonly }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {[1,2,3,4,5].map(n => (
        <button key={n} onClick={() => !readonly && onChange?.(n)} style={{
          background: "none", border: "none", cursor: readonly ? "default" : "pointer",
          fontSize: 28, lineHeight: 1, transition: "transform 0.1s",
          filter: n <= value ? "none" : "grayscale(1) opacity(0.3)",
          transform: n <= value ? "scale(1.1)" : "scale(1)",
        }}>⭐</button>
      ))}
      <span style={{ alignSelf: "center", color: C.textSub, fontSize: 13, marginLeft: 4 }}>
        {value ? `${value}/5` : ""}
      </span>
    </div>
  );
}

function WorkflowStepper({ status }) {
  const steps = ["draft", "submitted", "manager_reviewed", "finalized"];
  const labels = ["Draft", "Submitted", "Mgr Reviewed", "Finalized"];
  const cur = steps.indexOf(status);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, margin: "8px 0" }}>
      {steps.map((s, i) => (
        <div key={s} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "none" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              background: i < cur ? C.success : i === cur ? C.accent : C.border,
              border: `2px solid ${i < cur ? C.success : i === cur ? C.accent : C.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, color: "#fff", transition: "all 0.3s",
            }}>{i < cur ? "✓" : i + 1}</div>
            <span style={{ fontSize: 10, color: i <= cur ? C.text : C.muted, fontWeight: i === cur ? 600 : 400, whiteSpace: "nowrap" }}>{labels[i]}</span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ flex: 1, height: 2, background: i < cur ? C.success : C.border, margin: "0 4px", marginBottom: 18, transition: "background 0.3s" }} />
          )}
        </div>
      ))}
    </div>
  );
}

function EmptyState({ icon, title, sub, action }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif", marginBottom: 8 }}>{title}</div>
      <div style={{ color: C.textSub, fontSize: 14, marginBottom: action ? 24 : 0 }}>{sub}</div>
      {action}
    </div>
  );
}

function Table({ cols, rows, onRow }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c.key} style={{ padding: "10px 16px", textAlign: "left", color: C.textSub, fontWeight: 600, fontSize: 12, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} onClick={() => onRow?.(row)} style={{ borderBottom: `1px solid ${C.border}20`, cursor: onRow ? "pointer" : "default", transition: "background 0.15s" }}
              onMouseEnter={e => { if (onRow) e.currentTarget.style.background = `${C.accent}08`; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: "13px 16px", color: C.text, verticalAlign: "middle" }}>
                  {c.render ? c.render(row[c.key], row) : row[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// LOGIN PAGE
// ============================================================
function LoginPage() {
  const { login, loading } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const demoUsers = [
    { label: "HR Admin", email: "hr.admin@company.com" },
    { label: "Anil Gupta (Mgr)", email: "anil.gupta@company.com" },
    { label: "Rahul Sharma (Emp)", email: "rahul.sharma@company.com" },
    { label: "Priya Patel (Emp)", email: "priya.patel@company.com" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(form.email, form.password);
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ ...gs.page, display: "flex", minHeight: "100vh" }}>
      {/* Left panel */}
      <div style={{ flex: 1, background: `linear-gradient(135deg, ${C.accentD} 0%, #1e1b4b 50%, #0f1117 100%)`, display: "flex", flexDirection: "column", justifyContent: "center", padding: 60, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -100, left: -100, width: 400, height: 400, background: `${C.accent}20`, borderRadius: "50%", filter: "blur(80px)" }} />
        <div style={{ position: "absolute", bottom: -80, right: -80, width: 300, height: 300, background: `${C.accentD}30`, borderRadius: "50%", filter: "blur(60px)" }} />
        <div style={{ position: "relative" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📊</div>
          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 42, fontWeight: 700, lineHeight: 1.2, marginBottom: 16 }}>
            Performance<br />Review Hub
          </h1>
          <p style={{ color: `${C.text}99`, fontSize: 16, lineHeight: 1.6, maxWidth: 380 }}>
            Streamline your organization's performance review lifecycle — from self-assessment to final HR decisions.
          </p>
          <div style={{ marginTop: 40, display: "flex", flexDirection: "column", gap: 12 }}>
            {["Employee self-reviews with goal tracking", "Manager reviews with decision workflow", "HR analytics and cycle management"].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, color: `${C.text}cc` }}>
                <span style={{ color: C.success, fontWeight: 700 }}>✓</span> {f}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div style={{ width: 480, display: "flex", flexDirection: "column", justifyContent: "center", padding: 60, background: C.surface }}>
        <div style={{ marginBottom: 40 }}>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Welcome back</h2>
          <p style={{ color: C.textSub, fontSize: 14 }}>Sign in to your HRMS account</p>
        </div>

        {/* Demo shortcuts */}
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 12, color: C.textSub, marginBottom: 10, fontWeight: 600 }}>QUICK DEMO LOGIN</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {demoUsers.map(u => (
              <button key={u.email} onClick={() => setForm({ email: u.email, password: "Password@123" })}
                style={{ ...gs.btn("ghost", "sm"), justifyContent: "center", fontSize: 12 }}>
                {u.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <FormField label="Email Address" required>
            <input style={gs.input} type="email" value={form.email} placeholder="you@company.com"
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
          </FormField>
          <FormField label="Password" required>
            <input style={gs.input} type="password" value={form.password} placeholder="••••••••"
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
          </FormField>
          {err && <div style={{ color: C.danger, fontSize: 13, background: "#450a0a", border: "1px solid #7f1d1d", borderRadius: 8, padding: "10px 14px" }}>{err}</div>}
          <button type="submit" disabled={busy || loading} style={{ ...gs.btn("primary"), justifyContent: "center", padding: "13px 20px", marginTop: 4 }}>
            {busy ? <Spinner size={18} /> : "Sign In →"}
          </button>
        </form>
        <p style={{ color: C.textSub, fontSize: 12, textAlign: "center", marginTop: 24 }}>All demo passwords: Password@123</p>
      </div>
    </div>
  );
}

// ============================================================
// SIDEBAR NAV
// ============================================================
function Sidebar({ active, setActive, user }) {
  const { logout } = useAuth();
  const isEmployee = user?.role === "employee";
  const isManager  = user?.role === "manager";
  const isHR       = user?.role === "hr_admin";

  const navItems = [
    { id: "dashboard",      label: "Dashboard",      icon: "🏠", show: true },
    { id: "my-reviews",     label: "My Reviews",     icon: "📝", show: isEmployee },
    { id: "team-reviews",   label: "Team Reviews",   icon: "👥", show: isManager },
    { id: "all-reviews",    label: "All Reviews",    icon: "📋", show: isHR },
    { id: "cycles",         label: "Review Cycles",  icon: "🔄", show: isHR || isManager },
    { id: "analytics",      label: "Analytics",      icon: "📊", show: isHR || isManager },
    { id: "users",          label: "Users & Teams",  icon: "🏢", show: isHR },
    { id: "notifications",  label: "Notifications",  icon: "🔔", show: true },
  ].filter(n => n.show);

  const roleColor = { employee: C.success, manager: C.accent, hr_admin: C.warn };

  return (
    <div style={{ width: 240, background: C.surface, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", height: "100vh", position: "sticky", top: 0 }}>
      {/* Logo */}
      <div style={{ padding: "24px 20px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: C.accentL }}>⬡ HRMS</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Performance Review</div>
      </div>

      {/* User pill */}
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${roleColor[user?.role] || C.accent}22`, border: `2px solid ${roleColor[user?.role] || C.accent}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: roleColor[user?.role] }}>
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, truncate: true, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.first_name} {user?.last_name}</div>
            <div style={{ fontSize: 11, color: roleColor[user?.role], fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{user?.role?.replace("_", " ")}</div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "12px 12px", overflowY: "auto" }}>
        {navItems.map(item => (
          <button key={item.id} onClick={() => setActive(item.id)} style={{
            width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
            borderRadius: 10, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 500,
            background: active === item.id ? `${C.accent}20` : "transparent",
            color: active === item.id ? C.accentL : C.textSub,
            borderLeft: active === item.id ? `3px solid ${C.accent}` : "3px solid transparent",
            marginBottom: 2, transition: "all 0.15s", textAlign: "left",
          }}>
            <span style={{ fontSize: 16 }}>{item.icon}</span> {item.label}
          </button>
        ))}
      </nav>

      {/* Logout */}
      <div style={{ padding: "12px 12px", borderTop: `1px solid ${C.border}` }}>
        <button onClick={logout} style={{ ...gs.btn("ghost", "sm"), width: "100%", justifyContent: "center", color: C.danger, borderColor: `${C.danger}44` }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
function Dashboard({ user }) {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cyclesRes] = await Promise.all([cyclesApi.list({ status: "active", pageSize: 5 })]);
        setCycles(cyclesRes?.data || []);
        if (user.role !== "employee") {
          const s = await analyticsApi.summary();
          setStats(s?.data);
        }
      } catch (e) { toast(e.message, "error"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><Spinner size={40} /></div>;

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 30, fontWeight: 700 }}>
          Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {user.first_name} 👋
        </h1>
        <p style={{ color: C.textSub, fontSize: 15, marginTop: 6 }}>Here's what's happening with performance reviews today.</p>
      </div>

      {stats && (
        <>
          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
            {(() => {
              const byStatus = Object.fromEntries((stats.status_distribution || []).map(x => [x.status, x.count]));
              return [
                { label: "Total Reviews",    value: stats.ratings?.total_reviews || 0, icon: "📋", color: C.accentL },
                { label: "Pending Submission", value: byStatus.draft || 0,              icon: "✏️",  color: C.warn },
                { label: "Awaiting Manager",  value: byStatus.submitted || 0,           icon: "⏳",  color: C.accent },
                { label: "Finalized",         value: byStatus.finalized || 0,           icon: "✅",  color: C.success },
              ];
            })().map(s => <StatCard key={s.label} {...s} />)}
          </div>

          {/* Charts row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
            {/* Decisions pie */}
            <div style={gs.card}>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 20 }}>Decision Breakdown</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={stats.decision_distribution?.map(d => ({ name: d.final_decision?.replace("_"," ") || "N/A", value: d.count }))}
                    cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                    {(stats.decision_distribution || []).map((_, i) => (
                      <Cell key={i} fill={[C.success, C.warn, C.danger][i % 3]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Ratings bar */}
            <div style={gs.card}>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Avg Ratings</h3>
              <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: C.textSub }}>Self: <strong style={{ color: C.accentL }}>{stats.ratings?.avg_self_rating ?? "—"}</strong></span>
                <span style={{ fontSize: 13, color: C.textSub }}>Manager: <strong style={{ color: C.success }}>{stats.ratings?.avg_manager_rating ?? "—"}</strong></span>
              </div>
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={[
                  { name: "Self Rating", value: parseFloat(stats.ratings?.avg_self_rating || 0) },
                  { name: "Mgr Rating", value: parseFloat(stats.ratings?.avg_manager_rating || 0) },
                ]}>
                  <XAxis dataKey="name" tick={{ fill: C.textSub, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 5]} tick={{ fill: C.textSub, fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
                  <Bar dataKey="value" radius={[6,6,0,0]}>
                    <Cell fill={C.accent} />
                    <Cell fill={C.success} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Active cycles */}
      <div style={gs.card}>
        <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Active Review Cycles</h3>
        {cycles.length === 0
          ? <EmptyState icon="🔄" title="No active cycles" sub="HR will activate a review cycle to begin" />
          : cycles.map(c => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0", borderBottom: `1px solid ${C.border}20` }}>
              <div>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>Submission deadline: {c.submission_deadline} · Manager deadline: {c.manager_review_deadline}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 13, color: C.textSub }}>{c.review_count} reviews</span>
                <Badge status={c.status} />
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ============================================================
// MY REVIEWS (Employee view)
// ============================================================
function MyReviews() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [createCycleId, setCreateCycleId] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rr, cr] = await Promise.all([reviewsApi.list({ pageSize: 50 }), cyclesApi.list({ status: "active", pageSize: 10 })]);
      setReviews(rr?.data || []);
      setCycles(cr?.data || []);
    } catch (e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!createCycleId) return;
    setCreating(true);
    try {
      const res = await reviewsApi.create({ review_cycle_id: createCycleId });
      setReviews(r => [res.data, ...r]);
      setSelectedReview(res.data);
      setCreateModal(false);
      toast("Review created! Fill in your self-assessment.");
    } catch (e) { toast(e.message, "error"); }
    finally { setCreating(false); }
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><Spinner size={40} /></div>;

  if (selectedReview) {
    return <SelfReviewForm reviewId={selectedReview.id} onBack={() => { setSelectedReview(null); load(); }} />;
  }

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700 }}>My Performance Reviews</h1>
          <p style={{ color: C.textSub, fontSize: 14, marginTop: 4 }}>Track and submit your self-assessments</p>
        </div>
        <button onClick={() => setCreateModal(true)} style={gs.btn("primary")}>＋ Start Review</button>
      </div>

      {reviews.length === 0
        ? <EmptyState icon="📝" title="No reviews yet" sub="Start a review for an active cycle"
            action={<button onClick={() => setCreateModal(true)} style={gs.btn("primary")}>Start Your First Review</button>} />
        : <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {reviews.map(r => (
              <div key={r.id} style={{ ...gs.card, cursor: "pointer", transition: "border-color 0.2s" }}
                onClick={() => setSelectedReview(r)}
                onMouseEnter={e => e.currentTarget.style.borderColor = C.accent}
                onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", fontSize: 17 }}>{r.cycle_name}</div>
                    <div style={{ fontSize: 12, color: C.textSub, marginTop: 3 }}>Q{r.period_type?.slice(-1)} {r.fiscal_year}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {r.final_decision && <Badge status={r.final_decision} type="decision" />}
                    <Badge status={r.status} />
                  </div>
                </div>
                <WorkflowStepper status={r.status} />
                <div style={{ display: "flex", gap: 20, marginTop: 14, fontSize: 13, color: C.textSub }}>
                  {r.self_rating && <span>Your rating: <strong style={{ color: C.accentL }}>⭐ {r.self_rating}/5</strong></span>}
                  {r.manager_rating && <span>Manager rating: <strong style={{ color: C.success }}>⭐ {r.manager_rating}/5</strong></span>}
                  {r.self_submitted_at && <span>Submitted: {new Date(r.self_submitted_at).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
          </div>
      }

      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Start New Review">
        <FormField label="Select Review Cycle" required>
          <select style={gs.input} value={createCycleId} onChange={e => setCreateCycleId(e.target.value)}>
            <option value="">-- Choose a cycle --</option>
            {cycles.map(c => <option key={c.id} value={c.id}>{c.name} (deadline: {c.submission_deadline})</option>)}
          </select>
        </FormField>
        {cycles.length === 0 && <p style={{ color: C.warn, fontSize: 13, marginTop: 8 }}>No active cycles available. Contact HR.</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button style={gs.btn("ghost")} onClick={() => setCreateModal(false)}>Cancel</button>
          <button style={gs.btn("primary")} onClick={handleCreate} disabled={!createCycleId || creating}>
            {creating ? <Spinner size={16} /> : "Create Review"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// SELF REVIEW FORM
// ============================================================
function SelfReviewForm({ reviewId, onBack }) {
  const { toast } = useToast();
  const [review, setReview] = useState(null);
  const [form, setForm] = useState({ self_rating: 0, self_remarks: "", goals_achieved: [""], areas_of_improvement: "" });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [rr, hr] = await Promise.all([reviewsApi.get(reviewId), reviewsApi.getHistory(reviewId)]);
        setReview(rr.data);
        setHistory(hr.data || []);
        const d = rr.data;
        setForm({
          self_rating: d.self_rating || 0,
          self_remarks: d.self_remarks || "",
          goals_achieved: d.goals_achieved ? (typeof d.goals_achieved === "string" ? JSON.parse(d.goals_achieved) : d.goals_achieved) : [""],
          areas_of_improvement: d.areas_of_improvement || "",
        });
      } catch (e) { toast(e.message, "error"); }
      finally { setLoading(false); }
    })();
  }, [reviewId]);

  const save = async () => {
    setSaving(true);
    try {
      const goals = form.goals_achieved.filter(g => g.trim());
      const res = await reviewsApi.saveSelf(reviewId, { ...form, goals_achieved: goals });
      setReview(res.data);
      toast("Self-review saved!");
    } catch (e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await save();
      const res = await reviewsApi.submit(reviewId);
      setReview(res.data);
      toast("Review submitted for manager review! 🎉");
      const hr = await reviewsApi.getHistory(reviewId);
      setHistory(hr.data || []);
    } catch (e) { toast(e.message, "error"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><Spinner size={40} /></div>;

  const isLocked = review?.is_locked || review?.status === "manager_reviewed" || review?.status === "finalized";
  const canSubmit = review?.status === "draft" || review?.status === "submitted";
  const canEdit   = !isLocked;

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 860, margin: "0 auto" }}>
      <button onClick={onBack} style={{ ...gs.btn("ghost", "sm"), marginBottom: 20 }}>← Back to My Reviews</button>

      {/* Header */}
      <div style={{ ...gs.card, marginBottom: 20, background: `linear-gradient(135deg, ${C.accentD}22, ${C.card})` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700 }}>{review?.cycle_name}</h2>
            <p style={{ color: C.textSub, fontSize: 13, marginTop: 4 }}>Manager: {review?.mgr_first_name} {review?.mgr_last_name}</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {review?.final_decision && <Badge status={review.final_decision} type="decision" />}
            <Badge status={review?.status} />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <WorkflowStepper status={review?.status} />
        </div>
        {isLocked && <div style={{ marginTop: 12, background: "#451a0322", border: "1px solid #92400e55", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: C.warn }}>
          🔒 This review is locked — manager has submitted their evaluation.
        </div>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 20 }}>
        {/* Self Review Form */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={gs.card}>
            <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 18 }}>Self Assessment</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <FormField label="Self Rating" required>
                <RatingStars value={form.self_rating} onChange={v => setForm(f => ({ ...f, self_rating: v }))} readonly={!canEdit} />
              </FormField>

              <FormField label="Self Remarks" required>
                <textarea style={{ ...gs.input, minHeight: 110 }} value={form.self_remarks} disabled={!canEdit}
                  placeholder="Describe your performance this quarter..."
                  onChange={e => setForm(f => ({ ...f, self_remarks: e.target.value }))} />
              </FormField>

              <FormField label="Goals Achieved" required>
                {form.goals_achieved.map((g, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input style={{ ...gs.input, flex: 1 }} value={g} disabled={!canEdit}
                      placeholder={`Goal ${i + 1}...`}
                      onChange={e => setForm(f => {
                        const g2 = [...f.goals_achieved]; g2[i] = e.target.value;
                        return { ...f, goals_achieved: g2 };
                      })} />
                    {canEdit && i > 0 && <button onClick={() => setForm(f => ({ ...f, goals_achieved: f.goals_achieved.filter((_,j) => j !== i) }))}
                      style={{ ...gs.btn("danger", "sm"), padding: "8px 12px" }}>✕</button>}
                  </div>
                ))}
                {canEdit && <button style={{ ...gs.btn("ghost", "sm") }} onClick={() => setForm(f => ({ ...f, goals_achieved: [...f.goals_achieved, ""] }))}>+ Add Goal</button>}
              </FormField>

              <FormField label="Areas of Improvement" required>
                <textarea style={{ ...gs.input, minHeight: 90 }} value={form.areas_of_improvement} disabled={!canEdit}
                  placeholder="Where do you want to grow next quarter?"
                  onChange={e => setForm(f => ({ ...f, areas_of_improvement: e.target.value }))} />
              </FormField>

              {canEdit && (
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                  <button style={gs.btn("ghost")} onClick={save} disabled={saving}>
                    {saving ? <Spinner size={16} /> : "💾 Save Draft"}
                  </button>
                  {canSubmit && (
                    <button style={gs.btn("primary")} onClick={submit} disabled={submitting || !form.self_rating || !form.self_remarks}>
                      {submitting ? <Spinner size={16} /> : "Submit for Review →"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Manager review (read only) */}
          {review?.manager_rating && (
            <div style={{ ...gs.card, background: "#022c2222", borderColor: `${C.success}44` }}>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 16, color: C.success }}>Manager Evaluation</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div><label style={{ fontSize: 12, color: C.textSub, fontWeight: 600 }}>RATING</label><RatingStars value={review.manager_rating} readonly /></div>
                <div><label style={{ fontSize: 12, color: C.textSub, fontWeight: 600, display: "block", marginBottom: 6 }}>REMARKS</label>
                  <p style={{ fontSize: 14, lineHeight: 1.6, color: C.text }}>{review.manager_remarks}</p></div>
                <div><label style={{ fontSize: 12, color: C.textSub, fontWeight: 600, display: "block", marginBottom: 6 }}>DECISION</label>
                  <Badge status={review.final_decision} type="decision" /></div>
              </div>
            </div>
          )}
        </div>

        {/* Status history sidebar */}
        <div>
          <div style={gs.card}>
            <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Activity Timeline</h3>
            {history.map((h, i) => (
              <div key={h.id} style={{ display: "flex", gap: 12, marginBottom: 16, position: "relative" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  {i < history.length - 1 && <div style={{ width: 2, flex: 1, background: C.border, marginTop: 4 }} />}
                </div>
                <div style={{ flex: 1, paddingBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{h.first_name} {h.last_name}</div>
                  <div style={{ fontSize: 12, color: C.textSub, marginTop: 1 }}>
                    {h.from_status ? `${h.from_status} → ` : ""}<strong>{h.to_status}</strong>
                  </div>
                  {h.remarks && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{h.remarks}</div>}
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{new Date(h.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MANAGER REVIEW VIEW
// ============================================================
function TeamReviews() {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reviewsApi.list({ pageSize: 100 });
      setReviews(res?.data || []);
    } catch (e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === "all" ? reviews : reviews.filter(r => r.status === filter);

  if (selected) return <ManagerReviewForm reviewId={selected.id} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700 }}>Team Reviews</h1>
        <p style={{ color: C.textSub, fontSize: 14, marginTop: 4 }}>Review and evaluate your team members</p>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {[["all","All"],["submitted","Awaiting Review"],["manager_reviewed","Completed"],["finalized","Finalized"]].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{
            ...gs.btn("ghost", "sm"), borderColor: filter === v ? C.accent : C.border,
            color: filter === v ? C.accentL : C.textSub, background: filter === v ? `${C.accent}15` : "transparent",
          }}>{l} {v !== "all" && <span style={{ background: C.border, borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>{reviews.filter(r => r.status === v).length}</span>}</button>
        ))}
      </div>

      {loading ? <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={36} /></div>
       : filtered.length === 0 ? <EmptyState icon="👥" title="No reviews found" sub="Your team members haven't submitted reviews yet" />
       : (
        <Table
          cols={[
            { key: "emp_code", label: "EMP ID" },
            { key: "emp_first", label: "Employee", render: (_, r) => `${r.emp_first} ${r.emp_last}` },
            { key: "cycle_name", label: "Cycle" },
            { key: "self_rating", label: "Self Rating", render: v => v ? `⭐ ${v}/5` : "—" },
            { key: "status", label: "Status", render: v => <Badge status={v} /> },
            { key: "final_decision", label: "Decision", render: v => v ? <Badge status={v} type="decision" /> : "—" },
            { key: "self_submitted_at", label: "Submitted", render: v => v ? new Date(v).toLocaleDateString() : "—" },
          ]}
          rows={filtered}
          onRow={setSelected}
        />
      )}
    </div>
  );
}

// ============================================================
// MANAGER REVIEW FORM
// ============================================================
function ManagerReviewForm({ reviewId, onBack }) {
  const { toast } = useToast();
  const [review, setReview] = useState(null);
  const [form, setForm] = useState({ manager_rating: 0, manager_remarks: "", final_decision: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [rr, hr] = await Promise.all([reviewsApi.get(reviewId), reviewsApi.getHistory(reviewId)]);
        setReview(rr.data);
        setHistory(hr.data || []);
        if (rr.data.manager_rating) {
          setForm({ manager_rating: rr.data.manager_rating, manager_remarks: rr.data.manager_remarks || "", final_decision: rr.data.final_decision || "" });
        }
      } catch (e) { toast(e.message, "error"); }
      finally { setLoading(false); }
    })();
  }, [reviewId]);

  const submit = async () => {
    if (!form.manager_rating || !form.manager_remarks || !form.final_decision) {
      return toast("Please fill all fields before submitting.", "error");
    }
    setSubmitting(true);
    try {
      const res = await reviewsApi.managerReview(reviewId, form);
      setReview(res.data);
      toast("Manager review submitted! 🎉 Employee has been notified.");
      const hr = await reviewsApi.getHistory(reviewId);
      setHistory(hr.data || []);
    } catch (e) { toast(e.message, "error"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><Spinner size={40} /></div>;

  const goals = review?.goals_achieved ? (typeof review.goals_achieved === "string" ? JSON.parse(review.goals_achieved) : review.goals_achieved) : [];
  const alreadyReviewed = review?.status === "manager_reviewed" || review?.status === "finalized";

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 920, margin: "0 auto" }}>
      <button onClick={onBack} style={{ ...gs.btn("ghost", "sm"), marginBottom: 20 }}>← Back to Team Reviews</button>

      <div style={{ ...gs.card, marginBottom: 20, background: `linear-gradient(135deg, #1e1b4b44, ${C.card})` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700 }}>
              {review?.emp_first_name} {review?.emp_last_name}
            </h2>
            <p style={{ color: C.textSub, fontSize: 13, marginTop: 2 }}>{review?.cycle_name} · {review?.emp_email}</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {review?.final_decision && <Badge status={review.final_decision} type="decision" />}
            <Badge status={review?.status} />
          </div>
        </div>
        <div style={{ marginTop: 16 }}><WorkflowStepper status={review?.status} /></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Employee self review */}
        <div style={{ ...gs.card, background: "#0f1117" }}>
          <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, marginBottom: 16, color: C.accentL }}>Employee Self Assessment</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div><label style={{ fontSize: 11, color: C.textSub, fontWeight: 600 }}>SELF RATING</label><RatingStars value={review?.self_rating} readonly /></div>
            <div>
              <label style={{ fontSize: 11, color: C.textSub, fontWeight: 600, display: "block", marginBottom: 6 }}>REMARKS</label>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: C.text }}>{review?.self_remarks || "—"}</p>
            </div>
            <div>
              <label style={{ fontSize: 11, color: C.textSub, fontWeight: 600, display: "block", marginBottom: 8 }}>GOALS ACHIEVED</label>
              {goals.map((g, i) => <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, fontSize: 13 }}><span style={{ color: C.success }}>✓</span>{g}</div>)}
            </div>
            <div>
              <label style={{ fontSize: 11, color: C.textSub, fontWeight: 600, display: "block", marginBottom: 6 }}>AREAS OF IMPROVEMENT</label>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: C.text }}>{review?.areas_of_improvement || "—"}</p>
            </div>
          </div>
        </div>

        {/* Manager form */}
        <div style={gs.card}>
          <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, marginBottom: 16, color: alreadyReviewed ? C.success : C.text }}>
            {alreadyReviewed ? "✓ Your Evaluation (Submitted)" : "Your Evaluation"}
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <FormField label="Manager Rating" required>
              <RatingStars value={form.manager_rating} onChange={v => setForm(f => ({ ...f, manager_rating: v }))} readonly={alreadyReviewed} />
            </FormField>
            <FormField label="Manager Remarks" required>
              <textarea style={{ ...gs.input, minHeight: 110 }} value={form.manager_remarks} disabled={alreadyReviewed}
                placeholder="Provide detailed feedback on performance..."
                onChange={e => setForm(f => ({ ...f, manager_remarks: e.target.value }))} />
            </FormField>
            <FormField label="Final Decision" required>
              <select style={gs.input} value={form.final_decision} disabled={alreadyReviewed}
                onChange={e => setForm(f => ({ ...f, final_decision: e.target.value }))}>
                <option value="">-- Select decision --</option>
                <option value="approved">✅ Approved</option>
                <option value="needs_improvement">⚠️ Needs Improvement</option>
                <option value="rejected">❌ Rejected</option>
              </select>
            </FormField>
            {!alreadyReviewed && (
              <button style={{ ...gs.btn("primary"), justifyContent: "center", marginTop: 8 }} onClick={submit} disabled={submitting}>
                {submitting ? <Spinner size={16} /> : "Submit Manager Review →"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div style={{ ...gs.card, marginTop: 20 }}>
        <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Activity Timeline</h3>
        <div style={{ display: "flex", gap: 0 }}>
          {history.map((h, i) => (
            <div key={h.id} style={{ flex: 1, position: "relative", paddingRight: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: i === history.length - 1 ? C.accent : C.success, marginBottom: 8 }} />
              {i < history.length - 1 && <div style={{ position: "absolute", top: 5, left: 10, right: 0, height: 2, background: C.border }} />}
              <div style={{ fontSize: 12, fontWeight: 600 }}>{h.first_name} {h.last_name}</div>
              <div style={{ fontSize: 11, color: C.textSub }}>{h.to_status}</div>
              <div style={{ fontSize: 10, color: C.muted }}>{new Date(h.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HR: ALL REVIEWS
// ============================================================
function AllReviews() {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({});
  const [selected, setSelected] = useState(null);
  const [cycles, setCycles] = useState([]);

  const load = useCallback(async (f = filter) => {
    setLoading(true);
    try {
      const params = { pageSize: 100, ...Object.fromEntries(Object.entries(f).filter(([,v]) => v)) };
      const [rr, cr] = await Promise.all([reviewsApi.list(params), cyclesApi.list({ pageSize: 20 })]);
      setReviews(rr?.data || []);
      setCycles(cr?.data || []);
    } catch (e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, []);

  const handleFinalize = async (id) => {
    const notes = prompt("HR notes (optional):");
    if (notes === null) return;
    try {
      await reviewsApi.finalize(id, { hr_notes: notes });
      toast("Review finalized! ✅");
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  if (selected) return <SelfReviewForm reviewId={selected.id} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700 }}>All Reviews</h1>
          <p style={{ color: C.textSub, fontSize: 14, marginTop: 4 }}>Full visibility across all employees and cycles</p>
        </div>
        <button style={gs.btn("ghost", "sm")} onClick={() => {
          const url = `${BASE_URL}/analytics/export?${new URLSearchParams(Object.fromEntries(Object.entries(filter).filter(([,v])=>v)))}`;
          window.open(url, "_blank");
        }}>⬇ Export CSV</button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <select style={{ ...gs.input, width: 180 }} value={filter.cycle_id || ""} onChange={e => setFilter(f => ({ ...f, cycle_id: e.target.value }))}>
          <option value="">All Cycles</option>
          {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={{ ...gs.input, width: 180 }} value={filter.status || ""} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="manager_reviewed">Manager Reviewed</option>
          <option value="finalized">Finalized</option>
        </select>
        <select style={{ ...gs.input, width: 180 }} value={filter.decision || ""} onChange={e => setFilter(f => ({ ...f, decision: e.target.value }))}>
          <option value="">All Decisions</option>
          <option value="approved">Approved</option>
          <option value="needs_improvement">Needs Improvement</option>
          <option value="rejected">Rejected</option>
        </select>
        <button style={gs.btn("primary", "sm")} onClick={() => load(filter)}>Apply</button>
        <button style={gs.btn("ghost", "sm")} onClick={() => { setFilter({}); load({}); }}>Reset</button>
      </div>

      {loading ? <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={36} /></div>
       : reviews.length === 0 ? <EmptyState icon="📋" title="No reviews found" sub="Try adjusting filters" />
       : (
        <div style={gs.card}>
          <Table
            cols={[
              { key: "emp_code", label: "EMP", render: (_, r) => <span style={{ fontFamily: "monospace", fontSize: 12, color: C.muted }}>{r.emp_code}</span> },
              { key: "emp_first", label: "Employee", render: (_, r) => <strong>{r.emp_first} {r.emp_last}</strong> },
              { key: "mgr_first", label: "Manager",  render: (_, r) => `${r.mgr_first} ${r.mgr_last}` },
              { key: "cycle_name", label: "Cycle" },
              { key: "self_rating", label: "Self ⭐", render: v => v ? `${v}/5` : "—" },
              { key: "manager_rating", label: "Mgr ⭐", render: v => v ? `${v}/5` : "—" },
              { key: "status", label: "Status", render: v => <Badge status={v} /> },
              { key: "final_decision", label: "Decision", render: v => v ? <Badge status={v} type="decision" /> : "—" },
              { key: "id", label: "", render: (id, r) => (
                r.status === "manager_reviewed"
                  ? <button style={gs.btn("success", "sm")} onClick={e => { e.stopPropagation(); handleFinalize(id); }}>Finalize</button>
                  : null
              )},
            ]}
            rows={reviews}
            onRow={setSelected}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// REVIEW CYCLES
// ============================================================
function ReviewCycles() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name:"", period_type:"Q1", fiscal_year: new Date().getFullYear(), start_date:"", end_date:"", submission_deadline:"", manager_review_deadline:"", description:"" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cyclesApi.list({ pageSize: 50 });
      setCycles(res?.data || []);
    } catch (e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await cyclesApi.create({ ...form, fiscal_year: parseInt(form.fiscal_year) });
      toast("Review cycle created! 🎉");
      setModal(false);
      load();
    } catch (e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const changeStatus = async (id, status) => {
    try {
      await cyclesApi.changeStatus(id, status);
      toast(`Cycle moved to ${status}`);
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  const nextStatus = { draft: "active", active: "closed", closed: "archived" };
  const statusColors = { draft: C.muted, active: C.success, closed: C.warn, archived: C.danger };

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700 }}>Review Cycles</h1>
          <p style={{ color: C.textSub, fontSize: 14, marginTop: 4 }}>Manage performance review periods</p>
        </div>
        {user?.role === "hr_admin" && <button style={gs.btn("primary")} onClick={() => setModal(true)}>＋ New Cycle</button>}
      </div>

      {loading ? <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={36} /></div>
       : cycles.length === 0 ? <EmptyState icon="🔄" title="No cycles yet" sub="Create the first review cycle" />
       : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {cycles.map(c => (
            <div key={c.id} style={{ ...gs.card, borderLeft: `4px solid ${statusColors[c.status]}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                <div>
                  <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 18 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>FY {c.fiscal_year}</div>
                </div>
                <Badge status={c.status} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, color: C.textSub, marginBottom: 14 }}>
                <div>📅 Start: <strong style={{ color: C.text }}>{c.start_date}</strong></div>
                <div>📅 End: <strong style={{ color: C.text }}>{c.end_date}</strong></div>
                <div>✏️ Submit by: <strong style={{ color: C.text }}>{c.submission_deadline}</strong></div>
                <div>👔 Mgr by: <strong style={{ color: C.text }}>{c.manager_review_deadline}</strong></div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: C.textSub }}>{c.review_count} reviews</span>
                {user?.role === "hr_admin" && nextStatus[c.status] && (
                  <button style={gs.btn("ghost", "sm")} onClick={() => changeStatus(c.id, nextStatus[c.status])}>
                    → Move to {nextStatus[c.status]}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Create Review Cycle" width={620}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <FormField label="Cycle Name" required>
              <input style={gs.input} value={form.name} placeholder="e.g. Q3-2026" onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </FormField>
          </div>
          <FormField label="Period Type" required>
            <select style={gs.input} value={form.period_type} onChange={e => setForm(f => ({ ...f, period_type: e.target.value }))}>
              {["Q1","Q2","Q3","Q4","H1","H2","ANNUAL"].map(p => <option key={p}>{p}</option>)}
            </select>
          </FormField>
          <FormField label="Fiscal Year" required>
            <input style={gs.input} type="number" value={form.fiscal_year} onChange={e => setForm(f => ({ ...f, fiscal_year: e.target.value }))} />
          </FormField>
          <FormField label="Start Date" required>
            <input style={gs.input} type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
          </FormField>
          <FormField label="End Date" required>
            <input style={gs.input} type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
          </FormField>
          <FormField label="Submission Deadline" required>
            <input style={gs.input} type="date" value={form.submission_deadline} onChange={e => setForm(f => ({ ...f, submission_deadline: e.target.value }))} />
          </FormField>
          <FormField label="Manager Review Deadline" required>
            <input style={gs.input} type="date" value={form.manager_review_deadline} onChange={e => setForm(f => ({ ...f, manager_review_deadline: e.target.value }))} />
          </FormField>
          <div style={{ gridColumn: "1 / -1" }}>
            <FormField label="Description">
              <textarea style={{ ...gs.input, minHeight: 70 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description..." />
            </FormField>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button style={gs.btn("ghost")} onClick={() => setModal(false)}>Cancel</button>
          <button style={gs.btn("primary")} onClick={create} disabled={saving}>{saving ? <Spinner size={16} /> : "Create Cycle"}</button>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// ANALYTICS
// ============================================================
function Analytics() {
  const { toast } = useToast();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [cycleData, setCycleData] = useState(null);
  const [cycles, setCycles] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([analyticsApi.summary(), cyclesApi.list({ pageSize: 20 })]);
        setSummary(s?.data);
        setCycles(c?.data || []);
      } catch (e) { toast(e.message, "error"); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadCycle = async (id) => {
    setSelectedCycle(id);
    try {
      const res = await analyticsApi.cycle(id);
      setCycleData(res?.data);
    } catch (e) { toast(e.message, "error"); }
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><Spinner size={40} /></div>;

  const COLORS = [C.success, C.warn, C.danger];
  const decisionData = (summary?.decision_distribution || []).map(d => ({
    name: d.final_decision?.replace(/_/g," ") || "Pending", value: d.count
  }));

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700 }}>Analytics & Reports</h1>
        <p style={{ color: C.textSub, fontSize: 14, marginTop: 4 }}>Insights across all performance reviews</p>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 28 }}>
        {[
          { label: "Total Reviews", value: summary?.ratings?.total_reviews || 0, icon: "📋", color: C.accentL },
          { label: "Avg Self Rating", value: summary?.ratings?.avg_self_rating || "—", icon: "⭐", color: C.warn },
          { label: "Avg Mgr Rating", value: summary?.ratings?.avg_manager_rating || "—", icon: "🏆", color: C.success },
          { label: "Approved", value: (summary?.decision_distribution || []).find(d => d.final_decision === "approved")?.count || 0, icon: "✅", color: C.success },
        ].map(s => <StatCard key={s.label} {...s} />)}
      </div>

      {/* Charts row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Status distribution bar */}
        <div style={gs.card}>
          <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 20 }}>Status Distribution</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={(summary?.status_distribution || []).map(d => ({ name: d.status?.replace(/_/g," "), count: d.count }))}>
              <XAxis dataKey="name" tick={{ fill: C.textSub, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: C.textSub, fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
              <Bar dataKey="count" fill={C.accent} radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Decisions donut */}
        <div style={gs.card}>
          <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 20 }}>Decision Outcomes</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <ResponsiveContainer width={160} height={180}>
              <PieChart>
                <Pie data={decisionData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={4} dataKey="value">
                  {decisionData.map((_, i) => <Cell key={i} fill={COLORS[i % 3]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {decisionData.map((d, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: COLORS[i % 3] }} />
                  <span style={{ fontSize: 13, color: C.text }}>{d.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: COLORS[i % 3], marginLeft: 4 }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Per-cycle drill down */}
      <div style={gs.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16, fontWeight: 600 }}>Per-Cycle Breakdown</h3>
          <select style={{ ...gs.input, width: 200 }} value={selectedCycle || ""} onChange={e => loadCycle(e.target.value)}>
            <option value="">Select a cycle...</option>
            {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {(summary?.by_cycle || []).map(c => (
          <div key={c.name} style={{ padding: "14px 0", borderBottom: `1px solid ${C.border}20`, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 90, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", color: C.accentL }}>{c.name}</div>
            <div style={{ flex: 1, display: "flex", gap: 4 }}>
              {c.total > 0 && [
                { label: "Draft", count: c.draft, color: C.muted },
                { label: "Submitted", count: c.submitted, color: C.warn },
                { label: "Reviewed", count: c.manager_reviewed, color: C.accent },
                { label: "Finalized", count: c.finalized, color: C.success },
              ].map(s => s.count > 0 && (
                <div key={s.label} title={`${s.label}: ${s.count}`} style={{
                  height: 8, borderRadius: 4, flex: s.count,
                  background: s.color, transition: "flex 0.4s",
                }} />
              ))}
            </div>
            <div style={{ fontSize: 12, color: C.textSub, width: 120, textAlign: "right" }}>
              {c.total} reviews · {c.avg_mgr_rating ? `⭐ ${c.avg_mgr_rating}` : "No ratings"}
            </div>
          </div>
        ))}

        {cycleData && (
          <div style={{ marginTop: 20, padding: 20, background: "#0f1117", borderRadius: 12 }}>
            <h4 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Dept Breakdown for selected cycle</h4>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {(cycleData.by_department || []).map(d => (
                <div key={d.department} style={{ ...gs.card, flex: "0 0 180px" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14 }}>{d.department || "—"}</div>
                  <div style={{ fontSize: 12, color: C.textSub }}>Total: <strong style={{ color: C.text }}>{d.total}</strong></div>
                  <div style={{ fontSize: 12, color: C.textSub }}>Approved: <strong style={{ color: C.success }}>{d.approved}</strong></div>
                  <div style={{ fontSize: 12, color: C.textSub }}>Avg Rating: <strong style={{ color: C.warn }}>{d.avg_rating || "—"}</strong></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// USERS MANAGEMENT (HR Admin)
// ============================================================
function UsersPage() {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModal, setCreateModal] = useState(false);
  const [assignModal, setAssignModal] = useState(null);
  const [managers, setManagers] = useState([]);
  const [form, setForm] = useState({ email:"", password:"", first_name:"", last_name:"", role:"employee", department:"", designation:"" });
  const [assignMgrId, setAssignMgrId] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ur, mr] = await Promise.all([usersApi.list({ pageSize: 100 }), usersApi.list({ role: "manager", pageSize: 50 })]);
      setUsers(ur?.data || []);
      setManagers(mr?.data || []);
    } catch (e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await usersApi.create(form);
      toast("User created! ✅");
      setCreateModal(false);
      load();
    } catch (e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const assignMgr = async () => {
    if (!assignMgrId) return;
    setSaving(true);
    try {
      await usersApi.assignManager(assignModal.id, { manager_user_id: assignMgrId });
      toast("Manager assigned!");
      setAssignModal(null);
      load();
    } catch (e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try {
      await usersApi.toggleActive(id);
      toast("User status updated");
      load();
    } catch (e) { toast(e.message, "error"); }
  };

  const filtered = users.filter(u =>
    !search || `${u.first_name} ${u.last_name} ${u.email} ${u.employee_code}`.toLowerCase().includes(search.toLowerCase())
  );
  const roleColor = { employee: C.success, manager: C.accent, hr_admin: C.warn };

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700 }}>Users & Teams</h1>
          <p style={{ color: C.textSub, fontSize: 14, marginTop: 4 }}>Manage employees, managers and assignments</p>
        </div>
        <button style={gs.btn("primary")} onClick={() => setCreateModal(true)}>＋ Add User</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input style={{ ...gs.input, maxWidth: 320 }} placeholder="🔍 Search name, email, ID..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={36} /></div>
       : (
        <div style={gs.card}>
          <Table
            cols={[
              { key: "employee_code", label: "ID", render: v => <span style={{ fontFamily: "monospace", fontSize: 12, color: C.muted }}>{v}</span> },
              { key: "first_name", label: "Name", render: (_, u) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: `${roleColor[u.role]}22`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, color: roleColor[u.role], flexShrink: 0 }}>
                    {u.first_name[0]}{u.last_name[0]}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{u.first_name} {u.last_name}</div>
                    <div style={{ fontSize: 11, color: C.textSub }}>{u.email}</div>
                  </div>
                </div>
              )},
              { key: "role", label: "Role", render: v => <span style={{ color: roleColor[v], fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}>{v?.replace("_"," ")}</span> },
              { key: "department", label: "Dept" },
              { key: "designation", label: "Title" },
              { key: "is_active", label: "Status", render: v => <span style={{ color: v ? C.success : C.danger, fontWeight: 600 }}>{v ? "Active" : "Inactive"}</span> },
              { key: "id", label: "", render: (id, u) => (
                <div style={{ display: "flex", gap: 6 }}>
                  {u.role === "employee" && <button style={gs.btn("ghost", "sm")} onClick={e => { e.stopPropagation(); setAssignModal(u); setAssignMgrId(""); }}>Assign Mgr</button>}
                  <button style={{ ...gs.btn("ghost", "sm"), color: u.is_active ? C.danger : C.success }}
                    onClick={e => { e.stopPropagation(); toggle(id); }}>{u.is_active ? "Deactivate" : "Activate"}</button>
                </div>
              )},
            ]}
            rows={filtered}
          />
        </div>
      )}

      {/* Create user modal */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Add New User" width={600}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <FormField label="First Name" required><input style={gs.input} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} /></FormField>
          <FormField label="Last Name" required><input style={gs.input} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} /></FormField>
          <div style={{ gridColumn: "1/-1" }}>
            <FormField label="Email" required><input style={gs.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></FormField>
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <FormField label="Password" required>
              <input style={gs.input} type="password" value={form.password} placeholder="Min 8 chars, 1 uppercase, 1 number, 1 special" onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </FormField>
          </div>
          <FormField label="Role" required>
            <select style={gs.input} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="hr_admin">HR Admin</option>
            </select>
          </FormField>
          <FormField label="Department"><input style={gs.input} value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} /></FormField>
          <div style={{ gridColumn: "1/-1" }}>
            <FormField label="Designation"><input style={gs.input} value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} /></FormField>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button style={gs.btn("ghost")} onClick={() => setCreateModal(false)}>Cancel</button>
          <button style={gs.btn("primary")} onClick={create} disabled={saving}>{saving ? <Spinner size={16} /> : "Create User"}</button>
        </div>
      </Modal>

      {/* Assign manager modal */}
      <Modal open={!!assignModal} onClose={() => setAssignModal(null)} title={`Assign Manager — ${assignModal?.first_name} ${assignModal?.last_name}`} width={420}>
        <FormField label="Select Manager" required>
          <select style={gs.input} value={assignMgrId} onChange={e => setAssignMgrId(e.target.value)}>
            <option value="">-- Choose a manager --</option>
            {managers.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.department})</option>)}
          </select>
        </FormField>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button style={gs.btn("ghost")} onClick={() => setAssignModal(null)}>Cancel</button>
          <button style={gs.btn("primary")} onClick={assignMgr} disabled={saving || !assignMgrId}>{saving ? <Spinner size={16} /> : "Assign"}</button>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================
// NOTIFICATIONS
// ============================================================
function Notifications() {
  const { toast } = useToast();
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notifApi.list({ pageSize: 50 });
      setNotifs(res?.data || []);
      setUnread(res?.unread_count || 0);
    } catch (e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markAll = async () => {
    try { await notifApi.markAllRead(); setUnread(0); load(); } catch (e) { toast(e.message, "error"); }
  };

  const markOne = async (id) => {
    try { await notifApi.markRead(id); load(); } catch {}
  };

  const typeIcon = { REVIEW_SUBMITTED: "📝", MANAGER_REVIEW_DONE: "✅", REVIEW_READY_TO_FINALIZE: "📤", REVIEW_FINALIZED: "🏁", CYCLE_REMINDER: "⏰" };

  return (
    <div className="fade-up" style={{ padding: 32, maxWidth: 780, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700 }}>Notifications</h1>
          {unread > 0 && <p style={{ color: C.accent, fontSize: 14, marginTop: 4 }}>{unread} unread</p>}
        </div>
        {unread > 0 && <button style={gs.btn("ghost", "sm")} onClick={markAll}>Mark all read</button>}
      </div>

      {loading ? <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spinner size={36} /></div>
       : notifs.length === 0 ? <EmptyState icon="🔔" title="All caught up!" sub="No notifications yet" />
       : notifs.map(n => (
          <div key={n.id} onClick={() => !n.is_read && markOne(n.id)} style={{
            ...gs.card, marginBottom: 10, cursor: !n.is_read ? "pointer" : "default",
            borderLeft: !n.is_read ? `3px solid ${C.accent}` : `3px solid transparent`,
            background: !n.is_read ? `${C.accent}08` : C.card, transition: "all 0.2s",
          }}>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <span style={{ fontSize: 24, flexShrink: 0 }}>{typeIcon[n.type] || "📬"}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{n.title}</div>
                  {!n.is_read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.accent, flexShrink: 0, marginTop: 4 }} />}
                </div>
                <p style={{ color: C.textSub, fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{n.message}</p>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{new Date(n.created_at).toLocaleString()}</div>
              </div>
            </div>
          </div>
        ))
      }
    </div>
  );
}

// ============================================================
// MAIN APP SHELL
// ============================================================
function AppShell() {
  const { user } = useAuth();
  const [page, setPage] = useState("dashboard");

  const getDefaultPage = (role) => {
    if (role === "employee") return "my-reviews";
    return "dashboard";
  };

  useEffect(() => {
    if (user) setPage(getDefaultPage(user.role));
  }, [user?.role]);

  const renderPage = () => {
    switch (page) {
      case "dashboard":     return <Dashboard user={user} />;
      case "my-reviews":    return <MyReviews />;
      case "team-reviews":  return <TeamReviews />;
      case "all-reviews":   return <AllReviews />;
      case "cycles":        return <ReviewCycles />;
      case "analytics":     return <Analytics />;
      case "users":         return <UsersPage />;
      case "notifications": return <Notifications />;
      default:              return <Dashboard user={user} />;
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar active={page} setActive={setPage} user={user} />
      <main style={{ flex: 1, overflowY: "auto", background: C.bg }}>
        {renderPage()}
      </main>
    </div>
  );
}

// ============================================================
// ROOT
// ============================================================
export default function App() {
  return (
    <>
      <GlobalStyles />
      <ToastProvider>
        <AuthProvider>
          <Root />
        </AuthProvider>
      </ToastProvider>
    </>
  );
}

function Root() {
  const { user } = useAuth();
  return user ? <AppShell /> : <LoginPage />;
}
